import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu as MenuIcon, X } from "lucide-react";
import { BRAND } from "../data/menu";

const links = [
  { to: "/", label: "Home" },
  { to: "/menu", label: "Menu" },
  { to: "/gallery", label: "Gallery" },
  { to: "/locations", label: "Locations" },
  { to: "/reserve", label: "Reserve" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const onHero = location.pathname === "/" && !scrolled;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-cream/90 backdrop-blur-md border-b border-edge"
          : location.pathname === "/"
          ? "bg-transparent"
          : "bg-cream/90 backdrop-blur-md border-b border-edge"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo" className="flex flex-col leading-none">
          <span className={`font-serif text-3xl md:text-4xl tracking-tight transition-colors ${onHero ? "text-cream" : "text-charred"}`}>
            Old Soul <span className="italic text-ember">Pizzeria</span>
          </span>
          <span className={`hidden md:block text-[9px] uppercase tracking-[0.32em] mt-1 transition-colors ${onHero ? "text-cream/75" : "text-muted2"}`}>
            100 years of soul · 90 seconds on fire
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-xs uppercase tracking-[0.22em] transition-colors ${
                  isActive ? "text-ember" : onHero ? "text-cream hover:text-ember" : "text-charred hover:text-ember"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={BRAND.phoneHref}
            data-testid="navbar-phone"
            className={`text-xs uppercase tracking-[0.22em] transition-colors ${onHero ? "text-cream hover:text-ember" : "text-charred hover:text-ember"}`}
          >
            {BRAND.phone}
          </a>
        </nav>

        <button
          data-testid="navbar-menu-toggle"
          className={`md:hidden transition-colors ${onHero ? "text-cream" : "text-charred"}`}
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <MenuIcon size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-cream border-t border-edge"
          >
            <nav className="flex flex-col px-6 py-6 gap-5">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`nav-link-mobile-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `text-sm uppercase tracking-[0.22em] ${isActive ? "text-ember" : "text-charred"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <a href={BRAND.phoneHref} className="text-sm uppercase tracking-[0.22em] text-charred">
                {BRAND.phone}
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
